import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Typography,
  Paper,
  Divider,
  Chip,
  Grid,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  MenuItem,
  Select,
  SelectChangeEvent,
  Snackbar,
  Alert,
  AlertColor,
  CircularProgress,
  FormControl,
  InputLabel,
  InputAdornment,
  Stack
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Edit as EditIcon,
  Print as PrintIcon,
  Email as EmailIcon,
  Receipt as ReceiptIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Build as InProgressIcon,
  AccessTime as PendingIcon,
} from '@mui/icons-material';
import { format, parseISO } from 'date-fns';
import { getBookingById, updateBookingStatus, Booking, getInterestedSP, updateBookingInterestStatus  } from '../services/bookingService';
import { getStaffMembers, assignStaffToBooking, StaffMember } from '../services/staffService';
import BookingStatusHistory from '../components/booking/BookingStatusHistory';

type InterestedSP = {
  interest_id: number,
  sp_id: number;
  sp_name: string;
  sp_mobile: number;
  rating?: number;
  interest_status: string;
};

const BookingDetails = () => {
  const [interestedSP, setInterestedSP] = useState<InterestedSP[]>([]);
  const [selectedInterestedSP, setSelectedInterestedSP] = useState<number | ''>('');
  const [interestedLoading, setInterestedLoading] = useState(false);
  const [selectedInterestId, setSelectedInterestId] = useState<number | ''>('');
  
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  type BookingWithExtras = Booking & {
  services?: Array<{ name: string; price: number }>;
  subtotal?: number;
  tax_rate?: number;
  model_name?: string;
  brand_name?: string;
  number_plate?: string;
  assigned_staff?: {
    sp_id?: number;
    sp_name?: string;
    sp_email?: string | null;
    sp_mobile?: string | null;
    sp_country_code?: string | null;
    sp_profile_photo?: string | null;
  } | null;
};

  const [booking, setBooking] = useState<BookingWithExtras | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [staffLoading, setStaffLoading] = useState(false);
  const [staffMembers, setStaffMembers] = useState<StaffMember[]>([]);
  const [selectedStaff, setSelectedStaff] = useState<number | ''>('');
  const [tempSelectedStaff, setTempSelectedStaff] = useState<number | ''>('');
  const [availableStaff, setAvailableStaff] = useState<StaffMember[]>([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [dialogContent, setDialogContent] = useState({ 
    title: '', 
    message: '', 
    action: '' as 'confirm' | 'start' | 'complete' | 'cancel' | ''
  });
  
  // Snackbar state
  const [openSnackbar, setOpenSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'info' | 'warning'>('info');

  const statusOrder = [
  "pending",
  "confirmed",
  "assign_washer",
  "pickup",
  "work_in_progress",
  "drop",
  "arrived_at_the_location",
  "at_the_service_station",
  "start_service",
  "end_service",
  "completed",
  "cancelled", // keep at end if needed
];

const getAllowedStatuses = (currentStatus: string): string[] => {
  const currentIndex = statusOrder.indexOf(currentStatus);
  if (currentIndex === -1) return statusOrder; // fallback if unknown status
  return statusOrder.slice(currentIndex);
};

const isAlreadyAccepted = interestedSP.some(sp => sp.interest_status === "Accepted");

  const handleOpenDialog = (action: 'confirm' | 'start' | 'complete' | 'cancel') => {
    let title = '';
    let message = '';

    switch (action) {
      case 'confirm':
        title = 'Confirm Booking';
        message = 'Are you sure you want to confirm this booking?';
        break;
      case 'start':
        title = 'Start Service';
        message = 'Are you sure you want to start this service?';
        break;
      case 'complete':
        title = 'Complete Service';
        message = 'Are you sure you want to mark this service as completed?';
        break;
      case 'cancel':
        title = 'Cancel Booking';
        message = 'Are you sure you want to cancel this booking? This action cannot be undone.';
        break;
    }

    setDialogContent({
      title,
      message,
      action
    });
    setOpenDialog(true);
  };

  const handleStatusUpdate = async (newStatus: string) => {
    if (!booking || booking.status === newStatus) return;
    
    try {
      setLoading(true);
      await updateBookingStatus(booking.booking_id, newStatus);
      const updatedBooking = await getBookingById(booking.booking_id);
      setBooking(updatedBooking);
      
      setSnackbarMessage(`Status updated to ${newStatus.split('_').map(word => 
        word.charAt(0).toUpperCase() + word.slice(1)
      ).join(' ')}`);
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
      
      // If assigning staff, refresh the staff list to show updated availability
      if (newStatus === 'assign_washer' && selectedStaff) {
        const staff = await getStaffMembers();
        setStaffMembers(staff);
      }
    } catch (error) {
      console.error('Error updating booking status:', error);
      setSnackbarMessage('Failed to update booking status');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    } finally {
      setLoading(false);
    }
  };

  const handleDialogConfirm = () => {
    setOpenDialog(false);
    if (!booking) return;

    switch (dialogContent.action) {
      case 'confirm':
        handleStatusUpdate('confirmed');
        break;
      case 'start':
        handleStatusUpdate('work_in_progress');
        break;
      case 'complete':
        handleStatusUpdate('completed');
        break;
      case 'cancel':
        handleStatusUpdate('cancelled');
        break;
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        
        // Fetch booking data first
        if (id) {
          const bookingData = await getBookingById(parseInt(id));
          if (bookingData) {
            setBooking(bookingData);
            setSelectedStaff(bookingData.assigned_staff_id || '');

            const interested = await getInterestedSP(parseInt(id));
            setInterestedSP(interested);

          }
        }
        
        setError(null);
      } catch (err) {
        console.error('Error fetching booking data:', err);
        setError('Failed to load booking data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };

    const fetchStaffData = async () => {
      try {
        setStaffLoading(true);
        
        // Fetch staff members separately (this might fail if API doesn't support it)
        const staffData = await getStaffMembers();
        setStaffMembers(staffData);
        // Filter active staff members
        setAvailableStaff(staffData.filter(staff => staff.status === 'active'));
      } catch (err) {
        console.warn('Staff management not available:', err);
        // Don't show error for staff - just disable staff features
        setStaffMembers([]);
        setAvailableStaff([]);
      } finally {
        setStaffLoading(false);
      }
    };

    fetchData();
    fetchStaffData();
  }, [id]);

  const handleStaffChange = (event: SelectChangeEvent<number | ''>) => {
    setTempSelectedStaff(event.target.value as number);
  };
  
  const handleAssignStaff = async () => {
    if (!booking || tempSelectedStaff === '') return;
    
    const newStaffId = tempSelectedStaff;
    setSelectedStaff(newStaffId);
    
    try {
      await assignStaffToBooking(booking.booking_id, newStaffId);
      const updatedBooking = await getBookingById(booking.booking_id);
      setBooking(updatedBooking);
      
      // Refresh staff list to show updated availability
      const staff = await getStaffMembers();
      setStaffMembers(staff);
      
      setSnackbarMessage('Washer assigned successfully');
      setSnackbarSeverity('success');
      setOpenSnackbar(true);
    } catch (error) {
      console.error('Error assigning washer:', error);
      setSnackbarMessage('Failed to assign washer');
      setSnackbarSeverity('error');
      setOpenSnackbar(true);
    }
  };

  // Set initial tempSelectedStaff when booking loads
  useEffect(() => {
    if (booking && booking.assigned_staff_id) {
      setTempSelectedStaff(booking.assigned_staff_id);
    }
  }, [booking]);

  const formatDateTime = (dateString?: string, timeString?: string) => {
    if (!dateString) return 'N/A';
    
    try {
      const date = new Date(dateString);
      let formatted = format(date, 'dd/MM/yyyy');
      
      if (timeString) {
        const [hours, minutes] = timeString.split(':').map(Number);
        date.setHours(hours, minutes);
        formatted += ` at ${format(date, 'HH:mm')}`;
      }
      
      return formatted;
    } catch (error) {
      console.error('Error formatting date:', error);
      return 'Invalid date';
    }
  };

  const getStatusChip = (status: string) => {
    const statusMap: { [key: string]: { label: string; color: 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning' } } = {
      pending: { label: 'Pending', color: 'warning' },
      confirmed: { label: 'Confirmed', color: 'info' },
      assign_washer: { label: 'Assign Washer', color: 'info' },
      pickup: { label: 'Pickup', color: 'info' },
      work_in_progress: { label: 'In Progress', color: 'primary' },
      drop: { label: 'Drop', color: 'info' },
      arrived_at_the_location: { label: 'Arrived at Location', color: 'info' },
      at_the_service_station: { label: 'At Service Station', color: 'info' },
      start_service: { label: 'Service Started', color: 'info' },
      end_service: { label: 'Service Ended', color: 'info' },
      completed: { label: 'Completed', color: 'success' },
      cancelled: { label: 'Cancelled', color: 'error' },
      rejected: { label: 'Rejected', color: 'error' },
    };

    const statusInfo = statusMap[status] || { label: status, color: 'default' };
    
    return (
      <Chip 
        label={statusInfo.label}
        color={statusInfo.color}
        size="small"
        variant="outlined"
      />
    );
  };

  if (loading) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="300px">
          <CircularProgress />
        </Box>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <Box p={3}>
          <Alert severity="error">{error}</Alert>
        </Box>
      </div>
    );
  }
                    
  if (!booking) {
    return (
      <div className="container-xxl flex-grow-1 container-p-y">
        <Box p={3}>
          <Alert severity="error">Booking not found</Alert>
        </Box>
      </div>
    );
  }
const allowedStatuses = getAllowedStatuses(booking.status);

  return (
    <div className="container-xxl flex-grow-1 container-p-y">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <div className="d-flex align-items-center">
          <IconButton onClick={() => navigate(-1)} sx={{ mr: 2 }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h4" gutterBottom>
                                                   Bookings / Booking #{booking.booking_id.toString().padStart(4, '0')}
                                         </Typography>
        </div>
        <div>
          
          
        </div>
      </div>

      <div className="">
        <div className="">
          <Grid container spacing={3}>
            {/* Left Column - Main Content */}
            <Grid item xs={12} md={8}>
              <Paper 
                elevation={0}
                sx={{
                  p: 3,
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 1,
                  mb: 3
                }}
              >
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                  <Typography variant="h6">Booking Details</Typography>
                  <Box display="flex" gap={1}>
                  </Box>
                </Box>
                
                <Divider sx={{ mb: 3 }} />
                
                <Grid container spacing={2}>
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Contact Person
                    </Typography>
                    <Box>
                      <Typography variant="body1" fontWeight="500">
                        {booking.contact_person_name || 'N/A'}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {booking.contact_person_number ? `📞 ${booking.contact_person_number}` : 'Phone not available'}
                      </Typography>
                    </Box>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Booking ID
                    </Typography>
                    <Typography variant="body1">
                      #{booking.booking_number}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Status
                    </Typography>
                    <Box display="flex" alignItems="center" gap={1}>
                      {getStatusChip(booking.status)}
                    </Box>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Date & Time
                    </Typography>
                    <Typography variant="body1">
                      {formatDateTime(booking.booking_date, booking.start_time)}
                    </Typography>
                  </Grid>
                  
                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Duration
                    </Typography>
                    <Typography variant="body1">
                      {booking.start_time} - {booking.end_time}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Address
                    </Typography>
                    <Typography variant="body1">
                      {booking.address_line1}
                      {booking.address_line2 && `, ${booking.address_line2}`}
                      {booking.city && `, ${booking.city}`}
                      {booking.state && `, ${booking.state}`}
                      {booking.postal_code && ` ${booking.postal_code}`}
                    </Typography>
                  </Grid>

                  <Grid item xs={12} sm={6}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Vehicle
                    </Typography>
                    <Typography variant="body1">
                      {booking.model_name} - {booking.brand_name} 
                      ({booking.number_plate})
                    </Typography>
                  </Grid>

                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                      Services
                    </Typography>
                    <Box>
{booking.services && booking.services.length > 0 ? (
                        <ul style={{ margin: 0, paddingLeft: 20 }}>
                          {booking.services.map((service, index) => {
                            // Ensure price is a number before calling toFixed
                            const price = typeof service.price === 'number' ? service.price : 
                                       typeof service.price === 'string' ? parseFloat(service.price) : 0;
                            return (
                              <li key={index}>
                                <Typography variant="body1">
                                  {service.name} - {process.env.REACT_APP_SITE_CURRENCY}{price.toFixed(2)}
                                </Typography>
                              </li>
                            );
                          })}
                        </ul>
                      ) : (
                        <Typography variant="body2" color="textSecondary">
                          No services selected
                        </Typography>
                      )}
                    </Box>
                  </Grid>
                  
                  {booking.notes && (
                    <Grid item xs={12}>
                      <Typography variant="subtitle2" color="textSecondary">
                        Notes
                      </Typography>
                      <Typography variant="body2">
                        {booking.notes}
                      </Typography>
                    </Grid>
                  )}

                  {(booking.beforeImages && booking.beforeImages.length > 0) ||
                  (booking.afterImages && booking.afterImages.length > 0) ? (
                    <Grid item xs={12}>
                      <Box mt={2}>
                        <Typography variant="subtitle1" gutterBottom>
                          Before & After Images
                        </Typography>
                        <Grid container spacing={2}>
                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                              Before
                            </Typography>
                            {booking.beforeImages && booking.beforeImages.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {booking.beforeImages.map((img) => (
                                  <Box
                                    key={img.image_id}
                                    component="img"
                                    src={img.image_name_url}
                                    alt="Before image"
                                    sx={{
                                      width: 140,
                                      height: 140,
                                      objectFit: 'cover',
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                    }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                No before images
                              </Typography>
                            )}
                          </Grid>

                          <Grid item xs={12} md={6}>
                            <Typography variant="subtitle2" color="textSecondary" gutterBottom>
                              After
                            </Typography>
                            {booking.afterImages && booking.afterImages.length > 0 ? (
                              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
                                {booking.afterImages.map((img) => (
                                  <Box
                                    key={img.image_id}
                                    component="img"
                                    src={img.image_name_url}
                                    alt="After image"
                                    sx={{
                                      width: 140,
                                      height: 140,
                                      objectFit: 'cover',
                                      borderRadius: 1,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                    }}
                                  />
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="body2" color="textSecondary">
                                No after images
                              </Typography>
                            )}
                          </Grid>
                        </Grid>
                      </Box>
                    </Grid>
                  ) : null}
                </Grid>
                
                <Box mt={3}>
                  <Typography variant="subtitle1" gutterBottom>
                    Status History
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <BookingStatusHistory 
                    bookingId={booking.booking_id}
                    currentStatus={booking.status}
                    onStatusUpdate={() => {
                      if (id) {
                        getBookingById(parseInt(id)).then(updatedBooking => {
                          setBooking(updatedBooking);
                        });
                      }
                    }}
                  />
                </Box>
              </Paper>
            </Grid>
            
            {/* Right Column - Sidebar */}
            <Grid item xs={12} md={4}>
              <Box sx={{ top: 20, display: 'flex', flexDirection: 'column', gap: 3 }}>
                {/* Status Update Card */}
                {/* Show only if booking is not cancelled */}
   {booking.status !== "cancelled" && booking.status !== "completed" && (

  <>
                <Paper 
                  elevation={0}
                  sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="h6" gutterBottom>Update Status</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Select New Status</InputLabel>
<Select
  value={selectedStatus}
  onChange={(e) => setSelectedStatus(e.target.value)}
>
  {statusOrder.map((status) => (
    <MenuItem
      key={status}
      value={status}
      disabled={!allowedStatuses.includes(status)}
    >
      {status.replace(/_/g, " ").toUpperCase()}
    </MenuItem>
  ))}
</Select>

                    
                  </FormControl>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={() => selectedStatus && handleStatusUpdate(selectedStatus)}
                    disabled={!selectedStatus || !booking || selectedStatus === booking.status}
                  >
                    Update Status
                  </Button>
                </Paper>

                {/* Interested Service Providers Section */}

      {booking?.assigned_staff ? (
        <Paper
          elevation={0}
          sx={{
            p: 3,
            border: "1px solid",
            borderColor: "divider",
            borderRadius: 1,
            mb: 2
          }}
        >
          <Typography variant="h6" gutterBottom>
            Assigned Washer
          </Typography>
          <Divider sx={{ mb: 2 }} />

          <Box display="flex" alignItems="center" gap={2}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                {booking.assigned_staff.sp_name || 'N/A'}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                📞 {booking.assigned_staff.sp_country_code || ''} {booking.assigned_staff.sp_mobile || 'N/A'}
              </Typography>

              <Typography variant="body2" color="text.secondary">
                ✉️ {booking.assigned_staff.sp_email || 'N/A'}
              </Typography>
            </Box>
          </Box>
        </Paper>
      ) : null}


<Paper
  elevation={0}
  sx={{
    p: 3,
    border: "1px solid",
    borderColor: "divider",
    borderRadius: 1,
  }}
>
  <Typography variant="h6" gutterBottom>
    Interested Providers
  </Typography>
  <Divider sx={{ mb: 2 }} />

  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
    <InputLabel>Select SP</InputLabel>
    <Select
  value={selectedInterestedSP}
  onChange={(e) => {
    const spId = Number(e.target.value);

    setSelectedInterestedSP(spId);

    // find selected interest item
    const selected = interestedSP.find(sp => sp.sp_id === spId);
    setSelectedInterestId(selected?.interest_id || '');
  }}
  label="Select SP"
>

      {interestedSP.length === 0 && (
        <MenuItem value="">
          <em>No providers interested</em>
        </MenuItem>
      )}

{interestedSP.map((sp) => (
  <MenuItem key={sp.interest_id} value={sp.sp_id}>
    {sp.sp_name} — {sp.sp_mobile}
  </MenuItem>
))}

    </Select>
  </FormControl>
{!isAlreadyAccepted && (
<Button
  variant="contained"
  fullWidth
  disabled={!selectedInterestedSP}
  onClick={async () => {
    try {
      const spId = Number(selectedInterestedSP);

      // ASSIGN STAFF
      await assignStaffToBooking(booking.booking_id, spId);

      // UPDATE INTEREST STATUS
      await updateBookingInterestStatus({
        interest_id: Number(selectedInterestId),
        interest_status: "Accepted",
      });

      // REFRESH BOOKING
      const updatedBooking = await getBookingById(booking.booking_id);
      setBooking(updatedBooking);

      setTempSelectedStaff(spId);
      setSelectedStaff(spId);

      setSnackbarMessage("Service Provider accepted and assigned");
      setSnackbarSeverity("success");
      setOpenSnackbar(true);

    } catch (err) {
      console.error("Accept SP Error:", err);
      setSnackbarMessage("Failed to accept provider");
      setSnackbarSeverity("error");
      setOpenSnackbar(true);
    }
  }}
>
  Accept
</Button>
)}

</Paper>


                {/* Staff Assignment */}
                <Paper 
                  elevation={0}
                  sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="h6" gutterBottom>Assign Washer</Typography>
                  <Divider sx={{ mb: 2 }} />
                  <FormControl fullWidth size="small" sx={{ mb: 2 }}>
                    <InputLabel>Select Washer</InputLabel>
                    <Select
                      value={tempSelectedStaff}
                      onChange={handleStaffChange}
                      label="Select Washer"
                      size="small"
                      disabled={staffLoading}
                    >
                      <MenuItem value="">
                        <em>Unassigned</em>
                      </MenuItem>
                      {availableStaff.map((staff) => (
                        <MenuItem key={staff.id} value={staff.id}>
                          {staff.name} - {staff.phone}
                           ({staff.email})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <Button
                    variant="contained"
                    fullWidth
                    onClick={handleAssignStaff}
                    disabled={!tempSelectedStaff || tempSelectedStaff === selectedStaff || staffLoading}
                  >
                    {staffLoading ? 'Assigning...' : 'Assign Washer'}
                  </Button>
                </Paper>
                 </>
    )}
                {/* Payment Summary */}
                <Paper 
                  elevation={0}
                  sx={{
                    p: 3,
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 1
                  }}
                >
                  <Typography variant="h6" gutterBottom>Payment Summary</Typography>
                  <Divider sx={{ mb: 3 }} />
                  
                  <Box mb={2}>
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography>Subtotal:</Typography>
                      <Typography>{process.env.REACT_APP_SITE_CURRENCY}{(booking.total_amount || 0).toFixed(2)}</Typography>
                    </Box>
                    
                    {booking.discount_amount > 0 && (
                      <Box display="flex" justifyContent="space-between" mb={1}>
                        <Typography>Discount:</Typography>
                        <Typography color="error">-{process.env.REACT_APP_SITE_CURRENCY}{booking.discount_amount.toFixed(2)}</Typography>
                      </Box>
                    )}
                    
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography>Tax :</Typography>
                      <Typography>{process.env.REACT_APP_SITE_CURRENCY}{(booking.tax_amount || 0).toFixed(2)}</Typography>
                    </Box>
                    
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography>Tip :</Typography>
                      <Typography>{process.env.REACT_APP_SITE_CURRENCY}{(booking.tip_amount || 0).toFixed(2)}</Typography>
                    </Box>

                    <Divider sx={{ my: 2 }} />
                    
                    <Box display="flex" justifyContent="space-between" mb={1}>
                      <Typography variant="subtitle1">Total:</Typography>
                      <Typography variant="subtitle1" fontWeight="bold">
                        {process.env.REACT_APP_SITE_CURRENCY}{(booking.final_amount || 0).toFixed(2)}
                      </Typography>
                    </Box>
                    
                    <Box mt={2}>
                      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography variant="subtitle2" color="textSecondary">
                          Payment Status:
                        </Typography>
                        <Chip 
                          label={booking.payment_status === 'Success' ? 'Paid' : booking.payment_status} 
                          color={booking.payment_status === 'Success' ? 'success' : 'default'}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                      
                      {booking.payment_method && (
                        <Typography variant="body2" color="textSecondary">
                          Paid with {booking.payment_method}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                  
                  <Divider sx={{ my: 2 }} />
                  
                  <Box display="flex" flexDirection="column" gap={2}>
                    
                  </Box>
                  </Paper>
              </Box>

                              {/* ✅ Show extra details if booking is cancelled */}
{booking.status === "cancelled" && (
  <Box
    mt={3}
    p={2}
    borderRadius={2}
    border="1px solid #e0e0e0"
    bgcolor="#fafafa"
  >
    <Typography variant="h6" gutterBottom>
      Cancellation Summary
    </Typography>
<Divider sx={{ mb: 3 }} />
    <Box display="flex" justifyContent="space-between" mb={1}>
      <Typography variant="body2" color="text.secondary">
        Cancellation Reason
      </Typography>
      <Typography variant="body2" fontWeight={500} textAlign="right">
        {booking.cancellation_reason || "N/A"}
      </Typography>
    </Box>

    <Box display="flex" justifyContent="space-between" mb={1}>
      <Typography variant="body2" color="text.secondary">
        Refund Status
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {booking.refund_status || "N/A"}
      </Typography>
    </Box>

    <Box display="flex" justifyContent="space-between">
      <Typography variant="body2" color="text.secondary">
        Refund Amount
      </Typography>
      <Typography variant="body2" fontWeight={500}>
        {process.env.REACT_APP_SITE_CURRENCY}{booking.refund_amount || 0}
      </Typography>
    </Box>
  </Box>
)}
            </Grid>
          </Grid>
        </div>
      </div>
      
      {/* Confirmation Dialog */}
      <Dialog open={openDialog} onClose={() => setOpenDialog(false)}>
        <DialogTitle>{dialogContent.title}</DialogTitle>
        <DialogContent>
          <DialogContentText>{dialogContent.message}</DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Cancel</Button>
          <Button 
            onClick={handleDialogConfirm} 
            color="primary"
            variant="contained"
            autoFocus
          >
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={openSnackbar}
        autoHideDuration={6000}
        onClose={() => setOpenSnackbar(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert 
          onClose={() => setOpenSnackbar(false)} 
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </div>
  );
};

export default BookingDetails;